import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, UserIcon } from '@heroicons/react/24/outline';
import frFlag from '../assets/images/languages/fr.svg';
import gbFlag from '../assets/images/languages/gb.svg';
import easyLanguageLogo from '../assets/images/easylanguage_logo_clear.png';
import easyLanguageTitle from '../assets/images/easyLanguage_text_clear.png';

function Header() {
  const { t, i18n } = useTranslation();
  const { user, setUser, userProgress, interfaceLang } = useContext(UserContext);
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState({ code: 'fr', label: 'Français', flag: frFlag });

  const languageOptions = [
    { code: 'fr', label: 'Français', flag: frFlag },
    { code: 'en', label: 'English', flag: gbFlag }
  ];

  useEffect(() => {
    const selectedLanguage = languageOptions.find(lang => lang.code === i18n.language) || languageOptions[0];
    setCurrentLanguage(selectedLanguage);
  }, [i18n.language]);

  useEffect(() => {
    // Exemple de requête pour récupérer la progression utilisateur si nécessaire
    // if (user && !userProgress) {
    //   fetch(`/api/user-progress/${user.id}`)
    //     .then(response => response.json())
    //     .then(data => setUserProgress(data));
    // }
  }, [user, userProgress]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    navigate('/login');
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    const currentPath = window.location.pathname.split('/');
    currentPath[1] = lng;
    const newUrl = currentPath.join('/');
    window.history.replaceState(null, '', newUrl);
    navigate(`/${lng}${window.location.pathname.slice(3)}`);
  };

  return (
    <header className="bg-white p-4 shadow-md flex justify-between items-center sticky top-0 z-50">
      {/* Logo */}
      <a href="/" className="flex items-center space-x-2 justify-center" style={{ fontFamily: 'ChatterVOD' }}>
          <img src={easyLanguageLogo} alt="EasyLanguage Logo" className="h-6 md:h-10" />
          <img src={easyLanguageTitle} alt="EasyLanguage" className="h-6 md:h-10" />
      </a>
      {/* Language Dropdown */}
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className="inline-flex items-center justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none">
          <img src={currentLanguage.flag} alt={`${currentLanguage.label} flag`} className="w-6 h-6 rounded-full mr-2" />
          {currentLanguage.label}
          <ChevronDownIcon className="w-5 h-5 ml-2" />
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            {languageOptions.map(({ code, label, flag }) => (
              <Menu.Item key={code}>
                {({ active }) => (
                  <button
                    onClick={() => changeLanguage(code)}
                    className={`${active ? 'bg-gray-100' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                  >
                    <img src={flag} alt={`${label} flag`} className="w-5 h-5 rounded-full mr-2" /> {label}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>

      {/* User Info or Login */}
      <nav className="flex items-center space-x-4 mr-6">
        {user ? (
          <>
            {/* --- VERSION DESKTOP : BOX AVEC INFOS --- */}
            <div className="hidden md:flex items-center space-x-4 bg-white text-gray-800 p-4 rounded-lg shadow-lg">
              <div>
                <p className="text-blue-600 font-bold">
                  {t('username')}: <span className="font-semibold">{user.username}</span>
                </p>
                {userProgress ? (
                  <div className="mt-2">
                    <p className="text-sm">
                      {t('level')}: <span className="font-semibold">{userProgress.level}</span>
                    </p>
                    <p className="text-sm">
                      XP: <span className="font-semibold">{userProgress.xp_earned}</span>
                    </p>
                    <p className="text-sm">
                      {t('exercisesCompleted')}: <span className="font-semibold">{userProgress.exercises_completed}</span>
                    </p>
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-gray-500">
                    {t('loadingProgress')}
                  </div>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
              >
                {t('logout')}
              </button>
            </div>

            {/* --- VERSION MOBILE : SIMPLE ICÔNE --- */}
            <div className="md:hidden">
              <Link to={`/${interfaceLang}/profile`} className="bg-white p-2 rounded-full shadow-md hover:bg-gray-200">
                <UserIcon className="w-7 h-7 text-gray-600" />
              </Link>
            </div>
          </>
        ) : (
          // Si l'utilisateur n'est pas connecté, on garde le bouton de login
          <Link
            to={`/${interfaceLang}/login`}
            className="bg-white p-2 rounded-full shadow-md hover:bg-gray-200"
          >
            <UserIcon className="w-7 h-7 text-gray-600" />
          </Link>
        )}
      </nav>
    </header>
  );
}

export default Header;
